import React, { Component } from "react";
import PropTypes from "prop-types";
import { inject, observer } from "mobx-react";
import { airForm } from "../../../commonComponents";
import {Form, Row, Button, message} from "antd";
import "./CreditCardForm.css";
import {debounce, isFunction} from "lodash";
import api from "../../../services/api";
import Spin from "antd/es/spin";
import LoadingOverlay from "react-loading-overlay";

@inject("translationsStore", "step4RegistrationStore")
@observer
class CreditCardForm extends Component {
  zuoraDOMID = 'zuora_payment';

  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      renderingPaymentFrame: false,
      hideSubmitPaymentButton: false,
      lastPaymentErrorMessage: undefined
    };

    this.mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        // zuora hosted page payment is inside a frame
        if (mutation.target.tagName.toUpperCase() === 'IFRAME' && mutation.type === 'attributes' && mutation.attributeName === 'height') {
          this.setState({ renderingPaymentFrame: false });
        }
      });
    });
  }

  componentDidUpdate = () => {
    const zuoraDOM = document.getElementById(this.zuoraDOMID);
    if (zuoraDOM !== null) {
      this.mutationObserver.observe(zuoraDOM, {
        attributes: true,
        subtree: true
      });
    }
  };


  componentWillUnmount = () => {
    window.zuoraHostedSubmitFailed = undefined;
    window.zuoraHostedSubmitSucceed = undefined;
    window.zuoraHostedSubmitProcessing = undefined;

    this.mutationObserver.disconnect();
  };

  showErrorMessage = () => {
    const {
      translationsStore: { current: t }
    } = this.props;
    message.error(t['registration_4']['genericError']);
  };

  debouncedClearSubmitting = debounce(cb => {
    this.setState({ isSubmitting: false }, () => {
      isFunction(cb) && cb();
    });
  }, 1000);

  zuoraClientErrorMessageCallback = (key, code, message) => {
    this.debouncedClearSubmitting();
    window.Z.sendErrorMessageToHpm(key, message);
  };

  removeModalLayer = () => {
    this.setState({ isSubmitting: false, renderingPaymentFrame: false, hideSubmitPaymentButton: true });
  };

  zuoraServerErrorMessageCallback = callbackResponse => {
    this.setState({ isSubmitting: false, lastPaymentErrorMessage: callbackResponse.errorMessage });
  };

  zuoraHostedSubmitFailed = callbackResponse => {

    const loadMeWhenSubmitFailed = async () => {
      const params = await this.loadCreditCard();

      const callback = async response => {
        if (!response.success) {
          this.showErrorMessage();
          return;
        }
      };

      window.Z.renderWithErrorHandler(params, { creditCardCountry: 'ITA' }, callback, this.zuoraClientErrorMessageCallback, null, null, this.removeModalLayer);

      window.Z.runAfterRender(() => {
        this.zuoraServerErrorMessageCallback(callbackResponse);
      });
    };
    loadMeWhenSubmitFailed();
  };

  zuoraHostedSubmitSucceed = async callbackResponse => {
    this.setState({ isSubmitting: true });

    await api({action: 'setPaymentMethodDefault'}, this.props.step4RegistrationStore.user._id, callbackResponse.refId)
      .then(() => {
        this.props.afterCreditCardSubmission();
      })
      .catch(() => {
        this.showErrorMessage();
        this.loadPayment();
        this.setState({ hideSubmitPaymentButton: false });
      })
      .finally(() => this.setState({ isSubmitting: false }));
  };

  zuoraHostedSubmitProcessing = callbackResponse => {
    if (callbackResponse.errorCode) {
      this.showErrorMessage();
      this.setState({ hideSubmitPaymentButton: false })
    }
  };

  componentDidMount = () => {
    this.loadPayment();

    window.zuoraHostedSubmitFailed = this.zuoraHostedSubmitFailed;
    window.zuoraHostedSubmitSucceed = this.zuoraHostedSubmitSucceed;
    window.zuoraHostedSubmitProcessing = this.zuoraHostedSubmitProcessing;
    window.Z.setEventHandler("onCaptchaStateChange", this.removeModalLayer);
  };

  loadCreditCard = async () => {
    this.setState({ renderingPaymentFrame: true });

    const pageId = process.env.REACT_APP_ZUORA_HOSTED_PAGE_ID;
    const result = await api({ action: 'getZuoraToken' }, pageId)
      .catch(() => {
        this.showErrorMessage();
        this.loadPayment();
      });

    return {
      id: pageId,
      signature: result.signature,
      token: result.token,
      tenantId: result.tenantId,
      key: result.key,
      field_accountId: this.props.step4RegistrationStore.user.zuoraId,
      style: 'inline',
      submitEnabled: 'false',
      locale: 'it_IT',
      param_supportedTypes: 'Visa,MasterCard',
      url: process.env.REACT_APP_ZUORA_HOSTED_PAGE_URL,
      paymentGateway: process.env.REACT_APP_ZUORA_PAYMENT_GATEWAY
    };
  };

  loadPayment = () => {
    const loadMe = async () => {
      const params = await this.loadCreditCard();

      const callback = async response => {

        if (!response.success) {
          this.showErrorMessage();
          return;
        }
      };

      // render the Zuora Hosted Page
      window.Z.renderWithErrorHandler(params, { creditCardCountry: 'ITA' }, callback, this.zuoraClientErrorMessageCallback, null, null, this.removeModalLayer);
    };

    loadMe();
  };

  onSubmit = () => {
    this.setState(
      {
        isSubmitting: true,
        renderingPaymentFrame: true,
        lastPaymentErrorMessage: undefined
      },
      () => {
          window.Z.submit();
        }
    );
  }

  render() {
    const {
      translationsStore: { current: t }
    } = this.props;
    const {lastPaymentErrorMessage, isSubmitting, hideSubmitPaymentButton, renderingPaymentFrame} = this.state;

    return (
      <Form name="CreditCardForm" className="CreditCardForm">
        <LoadingOverlay
          active={this.state.renderingPaymentFrame || this.state.isSubmitting}
          spinner={<Spin tip={`${t['shared']['lblLoading']}`}/>}
          styles={{ overlay: base => ({ ...base, background: 'transparent' }) }}
        >
        <Row style={{ textAlign: 'center' }}>
          {(renderingPaymentFrame || isSubmitting) && <Spin tip={`${t['shared']['lblLoading']}`} />}
          {lastPaymentErrorMessage && (
            <div className="PaymentErrorMessage PaymentPane">
              {`${t['registration_4']['labelPaymentCallbackError']}: `}
              <i>{lastPaymentErrorMessage}</i>
            </div>
          )}
          <div id={this.zuoraDOMID} className="PaymentMethodContainer"><Spin /></div>
        </Row>
        </LoadingOverlay>
        <Row style={{ textAlign: 'center', minHeight: '35px' }}>
          {!hideSubmitPaymentButton && !isSubmitting && (
            <Button type="primary" onClick={this.onSubmit} loading={this.props.loading}>
              {t['shared']['btnContinue']}
            </Button>
          )}
        </Row>
      </Form>
    );
  }
}

CreditCardForm.propTypes = {
  afterCreditCardSubmission: PropTypes.func
};

export default Form.create()(airForm(CreditCardForm));



// WEBPACK FOOTER //
// ./src/components/RegistrationSteps/Step_4/CreditCardForm.js